import { Tooltip } from 'antd';
import {
  FARM_DIAGNOSTICS_LEGEND_ROLLING_AVG_LABEL,
  FARM_DIAGNOSTICS_LEGEND_YEAR_ON_YEAR_LABEL,
  FARM_DIAGNOSTICS_ROLLING_AVG_UNAVAILABLE_MESSAGE,
  FARM_DIAGNOSTICS_TREND_DECLINING,
  FARM_DIAGNOSTICS_TREND_IMPROVING,
  FARM_DIAGNOSTICS_TREND_STABLE,
} from 'constants/uiConstants';
import React from 'react';
import type { RollingAveragePoint, TrendDirection } from './computeFarmDiagnosticsMetrics';
import TrendBadge from './TrendBadge';

export interface TrendChartPoint {
  year: number;
  /** Null when this year has no value for this metric (e.g. no rainfall entered yet) — the thin year-on-year line breaks across it rather than drawing through a fabricated 0. */
  value: number | null;
}

export interface TrendChartMarker {
  year: number;
  tags: string[];
}

interface TrendChartProps {
  points: TrendChartPoint[];
  rollingAvg: RollingAveragePoint[];
  trendByYear: Record<number, TrendDirection | null>;
  markers?: TrendChartMarker[];
  formatValue: (value: number) => string;
  valueLabel: string;
  /** Whether to draw the thin year-on-year line/dots and its legend item. Defaults to true. */
  showYearOnYear?: boolean;
  /** Whether to draw the bold 3-yr rolling-average line/dots, its legend item, and the "unavailable" caption. Defaults to true. */
  showRollingAvg?: boolean;
}

const CHART_W = 700;
const CHART_H = 220;
const PAD_LEFT = 56;
const PAD_RIGHT = 12;
const PAD_TOP = 28;
const PAD_BOTTOM = 24;
const PLOT_W = CHART_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = CHART_H - PAD_TOP - PAD_BOTTOM;

const TREND_LINE_CLASS: Record<TrendDirection, string> = {
  [FARM_DIAGNOSTICS_TREND_IMPROVING]: 'stroke-success',
  [FARM_DIAGNOSTICS_TREND_STABLE]: 'stroke-gray-medium',
  [FARM_DIAGNOSTICS_TREND_DECLINING]: 'stroke-danger',
};

/**
 * Shared chart for the WUE Trend / Financial Efficiency / Bio vs Financial
 * dashboards: a thin light-gray year-on-year line, a bold 3-year
 * rolling-average line colored per segment by that segment's trend
 * classification, and vertical dashed markers at years carrying a
 * management-practice tag. Plain SVG, matching this codebase's existing
 * hand-rolled chart style (no charting library).
 */
const TrendChart: React.FC<TrendChartProps> = ({
  points,
  rollingAvg,
  trendByYear,
  markers = [],
  formatValue,
  valueLabel,
  showYearOnYear = true,
  showRollingAvg = true,
}) => {
  if (points.length === 0) return null;

  const values = (showYearOnYear ? points.map((p) => p.value) : [])
    .concat(showRollingAvg ? rollingAvg.map((p) => p.rollingAvg) : [])
    .filter((v): v is number => v != null);
  const maxValue = Math.max(1, ...values);
  const minValue = Math.min(0, ...values);
  const range = maxValue - minValue || 1;

  const x = (index: number) =>
    points.length > 1
      ? PAD_LEFT + (index / (points.length - 1)) * PLOT_W
      : PAD_LEFT + PLOT_W / 2;
  const y = (value: number) =>
    PAD_TOP + PLOT_H - ((value - minValue) / range) * PLOT_H;

  const gridFractions = [0, 0.5, 1];

  // Year-on-year thin line — broken into contiguous runs so a null value
  // (no data that year) leaves a visible gap instead of interpolating
  // through a fabricated point.
  const yoySegments: string[] = [];
  if (showYearOnYear) {
    let current: string[] = [];
    points.forEach((p, i) => {
      if (p.value == null) {
        if (current.length > 1) yoySegments.push(current.join(' '));
        current = [];
        return;
      }
      current.push(`${x(i)},${y(p.value)}`);
    });
    if (current.length > 1) yoySegments.push(current.join(' '));
  }

  const rollingAvgByYear = new Map(rollingAvg.map((p) => [p.year, p.rollingAvg]));
  const hasAnyRollingAvg =
    showRollingAvg && rollingAvg.some((p) => p.rollingAvg != null);

  // Bold rolling-average line — one <line> per adjacent pair that both have
  // a value, colored by the later point's own trend classification (falls
  // back to the neutral/stable color when that year's trend isn't
  // classified, e.g. the very first rolling-avg point with no N-1 to
  // compare against).
  const rollingAvgSegments = showRollingAvg
    ? points.slice(1).map((p, i) => {
        const prevPoint = points[i];
        const prevValue = rollingAvgByYear.get(prevPoint.year);
        const value = rollingAvgByYear.get(p.year);
        if (prevValue == null || value == null) return null;
        const trend = trendByYear[p.year];
        return {
          key: p.year,
          x1: x(i),
          y1: y(prevValue),
          x2: x(i + 1),
          y2: y(value),
          colorClass: TREND_LINE_CLASS[trend ?? FARM_DIAGNOSTICS_TREND_STABLE],
        };
      })
    : [];

  const markerByYear = new Map(markers.map((m) => [m.year, m.tags]));

  return (
    <div className="max-w-3xl">
      <span className="text-sm font-semibold text-charcoal">{valueLabel}</span>
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="mt-1 h-56 w-full">
        {gridFractions.map((f) => {
          const gy = PAD_TOP + PLOT_H - f * PLOT_H;
          const value = minValue + f * range;
          return (
            <g key={f}>
              <line
                x1={PAD_LEFT}
                y1={gy}
                x2={CHART_W - PAD_RIGHT}
                y2={gy}
                className="stroke-gray-bright"
                strokeWidth={1}
              />
              <text
                x={PAD_LEFT - 8}
                y={gy + 3}
                textAnchor="end"
                className="fill-gray-dark text-[0.6875rem] font-semibold"
              >
                {formatValue(value)}
              </text>
            </g>
          );
        })}

        {points.map((p, i) => {
          const tags = markerByYear.get(p.year);
          if (!tags || tags.length === 0) return null;
          return (
            <g key={`marker-${p.year}`}>
              <line
                x1={x(i)}
                y1={PAD_TOP}
                x2={x(i)}
                y2={CHART_H - PAD_BOTTOM}
                className="stroke-gray-dark"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              <Tooltip title={tags.join(', ')}>
                <text
                  x={x(i)}
                  y={PAD_TOP - 10}
                  textAnchor="middle"
                  className="cursor-help fill-gray-dark text-[0.5625rem] font-semibold"
                >
                  {tags[0]}
                  {tags.length > 1 ? ` +${tags.length - 1}` : ''}
                </text>
              </Tooltip>
            </g>
          );
        })}

        {points.map((p, i) => (
          <text
            key={p.year}
            x={x(i)}
            y={CHART_H - PAD_BOTTOM + 16}
            textAnchor="middle"
            className="fill-gray-dark text-[0.6875rem] font-semibold"
          >
            {p.year}
          </text>
        ))}

        {yoySegments.map((segPoints, i) => (
          <polyline
            key={i}
            points={segPoints}
            fill="none"
            className="stroke-gray-bright"
            strokeWidth={1.5}
          />
        ))}

        {rollingAvgSegments.map(
          (seg) =>
            seg && (
              <line
                key={seg.key}
                x1={seg.x1}
                y1={seg.y1}
                x2={seg.x2}
                y2={seg.y2}
                className={seg.colorClass}
                strokeWidth={3}
              />
            )
        )}

        {showYearOnYear &&
          points.map((p, i) => {
            if (p.value == null) return null;
            return (
              <Tooltip key={p.year} title={`${p.year}: ${formatValue(p.value)}`}>
                <circle
                  cx={x(i)}
                  cy={y(p.value)}
                  r={3}
                  className="cursor-help fill-gray-muted stroke-white"
                  strokeWidth={1}
                />
              </Tooltip>
            );
          })}

        {showRollingAvg &&
          rollingAvg.map((p, i) => {
            if (p.rollingAvg == null) return null;
            const trend = trendByYear[p.year];
            const dotClass = TREND_LINE_CLASS[trend ?? FARM_DIAGNOSTICS_TREND_STABLE];
            return (
              <Tooltip
                key={`avg-${p.year}`}
                title={`${p.year} (3-yr avg): ${formatValue(p.rollingAvg)}`}
              >
                <circle
                  cx={x(i)}
                  cy={y(p.rollingAvg)}
                  r={4.5}
                  className={`cursor-help stroke-white ${dotClass.replace('stroke-', 'fill-')}`}
                  strokeWidth={1.5}
                />
              </Tooltip>
            );
          })}
      </svg>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {showYearOnYear && (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
            <span className="inline-block h-0.5 w-4 bg-gray-bright" />
            {FARM_DIAGNOSTICS_LEGEND_YEAR_ON_YEAR_LABEL}
          </span>
        )}
        {showRollingAvg && (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
            {FARM_DIAGNOSTICS_LEGEND_ROLLING_AVG_LABEL}:
            <TrendBadge trend={FARM_DIAGNOSTICS_TREND_IMPROVING} />
            <TrendBadge trend={FARM_DIAGNOSTICS_TREND_STABLE} />
            <TrendBadge trend={FARM_DIAGNOSTICS_TREND_DECLINING} />
          </span>
        )}
      </div>

      {showRollingAvg && !hasAnyRollingAvg && (
        <span className="text-xs text-gray-muted">
          {FARM_DIAGNOSTICS_ROLLING_AVG_UNAVAILABLE_MESSAGE}
        </span>
      )}
    </div>
  );
};

export default TrendChart;
